import React, { useEffect, useState } from "react";
import Button, { ButtonType } from "../Assets/Button";
import { IServicePartner } from "../../models/serviceManagement.model";
import { Edit } from "lucide-react";
import { toast } from "react-hot-toast";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  getStatesData,
  getTalukasByDistrict,
} from "../../store/slices/kanbanSlice";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  partner: IServicePartner | null;
  loading: boolean;
  onUpdate: (id: string, payload: Partial<IServicePartner>) => Promise<void>;
}

const ViewServicePartnerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  partner,
  loading,
  onUpdate,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<IServicePartner | null>(null);

  const dispatch = useDispatch<RootDispatch>();

  const statesData = useSelector((state: RootState) => state.kanban.statesData);

  const talukasData = useSelector(
    (state: RootState) => state.kanban.talukasData,
  );

  const loadingTalukas = useSelector(
    (state: RootState) => state.kanban.loadingTalukas,
  );

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  useEffect(() => {
    if (formData?.address?.district) {
      dispatch(getTalukasByDistrict(formData.address.district));
    }
  }, [dispatch, formData?.address?.district]);

  useEffect(() => {
    if (partner) {
      setFormData(partner);
      setEditMode(false);
    }
  }, [partner]);

  if (!isOpen || !partner || !formData) return null;

  const validateForm = (): boolean => {
    if (!formData.name?.trim()) {
      toast.error("Name is required");
      return false;
    }

    if (!formData.mobileNumber?.trim()) {
      toast.error("Mobile number is required");
      return false;
    }

    if (!/^\d{10}$/.test(formData.mobileNumber)) {
      toast.error("Mobile number must be 10 digits");
      return false;
    }

    if (!formData.address?.district?.trim()) {
      toast.error("District is required");
      return false;
    }

    if (!formData.address?.pincode?.trim()) {
      toast.error("Pincode is required");
      return false;
    }

    if (!/^\d{6}$/.test(formData.address.pincode)) {
      toast.error("Pincode must be 6 digits");
      return false;
    }

    return true;
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const resetFields = () => {
    if (partner) {
      setFormData(partner);
    }
  };

  const closeModal = () => {
    resetFields();
    setEditMode(false);
    onClose();
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await onUpdate(partner._id ?? "", {
        name: formData.name,
        mobileNumber: formData.mobileNumber,
        companyName: formData.companyName,
        address: formData.address,
      });
      setEditMode(false);
    } catch (err: any) {
      console.error("Failed to update service partner:", err);
      return;
    }
  };

  const DetailRow = ({ label, value }: any) => (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-gray-800 mt-1">{value || "-"}</p>
    </div>
  );

  const inputStyle =
    "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="bg-white w-[650px] rounded-2xl p-8 shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Service Partner Details
          </h2>
          <div className="flex items-center gap-6">
            {!editMode ? (
              <Edit
                className="w-4 h-4 text-gray-800 cursor-pointer"
                onClick={() => setEditMode(true)}
              />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditMode(false);
                    resetFields();
                  }}
                  className="px-4 py-1 text-xs font-medium bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 text-lg"
            >
              ✕
            </button>
          </div>
        </div>

        {/* ================= VIEW MODE ================= */}
        {!editMode && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <DetailRow label="Partner Name" value={formData.name} />
              <DetailRow label="Mobile Number" value={formData.mobileNumber} />
              <DetailRow label="Company Name" value={formData.companyName} />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-3 border-b pb-2">
                Address Information
              </h3>

              <div className="grid grid-cols-2 gap-6">
                <DetailRow
                  label="Address"
                  value={formData.address.addressLine1}
                />
                <DetailRow label="District" value={formData.address.district} />
                <DetailRow label="Taluka" value={formData.address.taluka} />
                <DetailRow label="Pincode" value={formData.address.pincode} />
              </div>
            </div>
          </div>
        )}

        {/* ================= EDIT MODE ================= */}
        {editMode && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="text-sm text-gray-600">Partner Name</label>
                <input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={inputStyle}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Mobile Number</label>
                <input
                  value={formData.mobileNumber}
                  onChange={(e) => handleChange("mobileNumber", e.target.value)}
                  className={inputStyle}
                />
              </div>

              <div className="col-span-2">
                <label className="text-sm text-gray-600">Company Name</label>
                <input
                  value={formData.companyName || ""}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  className={inputStyle}
                />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-3 border-b pb-2">
                Address Information
              </h3>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-gray-600">Address Line</label>
                  <input
                    placeholder="Address Line"
                    value={formData.address.addressLine1 || ""}
                    onChange={(e) =>
                      handleAddressChange("addressLine1", e.target.value)
                    }
                    className={`${inputStyle} col-span-2`}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600">State</label>
                  <select
                    value={formData.address.state?.toLowerCase() || ""}
                    onChange={(e) =>
                      handleAddressChange("state", e.target.value)
                    }
                    className={inputStyle}
                  >
                    <option value="">Select State</option>
                    {statesData?.map((s) => (
                      <option key={s.state} value={s.state.toLowerCase()}>
                        {s.state.capitalizeWords()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600">District</label>
                  <select
                    value={formData.address.district?.toLowerCase() || ""}
                    onChange={(e) =>
                      handleAddressChange("district", e.target.value)
                    }
                    disabled={!formData.address.state}
                    className={inputStyle}
                  >
                    <option value="">Select District</option>
                    {statesData
                      .find(
                        (s) =>
                          s.state.toLowerCase() ===
                          formData.address.state?.toLowerCase(),
                      )
                      ?.districts?.map((district: string) => (
                        <option key={district} value={district}>
                          {district.capitalizeWords()}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600">Taluka</label>

                  <select
                    value={formData.address.taluka?.toLowerCase() || ""}
                    onChange={(e) =>
                      handleAddressChange("taluka", e.target.value)
                    }
                    disabled={!formData.address.district || loadingTalukas}
                    className={inputStyle}
                  >
                    <option value="">
                      {loadingTalukas
                        ? "Loading talukas..."
                        : talukasData?.taluka?.length
                          ? "Select Taluka"
                          : "No taluka available"}
                    </option>

                    {talukasData?.taluka?.map((taluka: string) => (
                      <option key={taluka} value={taluka}>
                        {taluka.capitalizeWords()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-600">Pincode</label>
                  <input
                    placeholder="Pincode"
                    value={formData.address.pincode}
                    onChange={(e) =>
                      handleAddressChange("pincode", e.target.value)
                    }
                    className={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          {editMode && (
            <Button
              type={ButtonType.Submit}
              onClick={handleUpdate}
              disabled={loading}
              className="text-xs"
            >
              {loading ? "Updating..." : "Save Changes"}
            </Button>
          )}
          <Button
            type={ButtonType.Cancel}
            onClick={closeModal}
            className="text-xs"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewServicePartnerModal;
